import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import BettingLines from './BettingLines'
import ScorecardMatrix from './ScorecardMatrix'

export default function MatchLobby({ session, matchId, onBack, onOpenScorecard, onInviteMore }) {
  const [match, setMatch] = useState(null)
  const [players, setPlayers] = useState([])
  const [profiles, setProfiles] = useState({})
  const [scorecards, setScorecards] = useState({})
  const [handicaps, setHandicaps] = useState({})
  const [golfProfiles, setGolfProfiles] = useState({})
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState(null)
  const [agreeLoading, setAgreeLoading] = useState(false)
  const [withdrawLoading, setWithdrawLoading] = useState(false)

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    fetchAll()
  }, [matchId])

  // Smart polling based on scorecard status
  useEffect(() => {
    const hasLive = Object.values(scorecards).some(sc => sc.status === 'live')
    const hasReview = Object.values(scorecards).some(sc => sc.status === 'review')
    
    if (hasLive) {
      // Active round — poll every 60 seconds
      const interval = setInterval(fetchAll, 60000)
      return () => clearInterval(interval)
    } else if (hasReview) {
      // Review window — poll every 5 minutes
      const interval = setInterval(fetchAll, 300000)
      return () => clearInterval(interval)
    }
    // No active scorecards — no polling
  }, [scorecards])

  const fetchAll = async () => {
    setLoading(true)

    const { data: m } = await supabase.from('matches').select('*').eq('id', matchId).single()
    setMatch(m)

    const { data: mp } = await supabase.from('match_players').select('*').eq('match_id', matchId)
    const mpSafe = mp || []
    setPlayers(mpSafe)

    const creatorId = m?.creator_id
    const allIds = [...new Set([session.user.id, ...(creatorId ? [creatorId] : []), ...mpSafe.map(p => p.player_id)].filter(Boolean))]

    const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name, username').in('id', allIds.filter(Boolean))
    const pmap = {}
    prof?.forEach(p => { pmap[p.id] = p })
    setProfiles(pmap)

    const { data: sc } = await supabase.from('scorecards').select('*').eq('match_id', matchId)
    const scMap = {}
    sc?.forEach(s => { scMap[s.player_id] = s })
    setScorecards(scMap)

    const { data: gp } = await supabase.from('golf_profiles').select('id, handicap_index, hcp_type, ghin_number, valid_rounds').in('id', allIds)
    const hMap = {}
    gp?.forEach(g => { hMap[g.id] = g.handicap_index != null ? g.handicap_index : null })
    setGolfProfiles(Object.fromEntries((gp || []).map(g => [g.id, g])))
    setHandicaps(hMap)

    // Auto-close scorecards past 24hr review window
    const now = Date.now()
    for (const sc of Object.values(scMap)) {
      if (sc.status === 'review' && sc.review_started_at) {
        const elapsed = now - new Date(sc.review_started_at).getTime()
        if (elapsed >= 24 * 60 * 60 * 1000) {
          await supabase.from('scorecards').update({ status: 'closed', closed_at: new Date().toISOString() }).eq('id', sc.id)
          scMap[sc.player_id] = { ...sc, status: 'closed' }
        }
      }
    }
    setScorecards(scMap)

    setLoading(false)
  }

  const handleAgree = async () => {
    const myCard = scorecards[session.user.id]
    if (!myCard) return showToast('You need to submit your scorecard first', 'error')
    if (myCard.status === 'live') return showToast('Submit your scorecard before agreeing', 'error')
    setAgreeLoading(true)
    const newAgreed = [...new Set([...(myCard.agreed_by || []), session.user.id])]
    await supabase.from('scorecards').update({ agreed_by: newAgreed }).eq('id', myCard.id)
    setAgreeLoading(false)
    showToast('You agreed to the scorecards', 'success')
    fetchAll()
  }

  const handleKickVote = async (targetPlayerId) => {
    const myCard = scorecards[session.user.id]
    if (!myCard || myCard.status === 'live') return showToast('Submit your scorecard before voting', 'error')

    // Find the target player's match_players record
    const target = players.find(p => p.player_id === targetPlayerId)
    if (!target) return

    const currentVotes = target.kick_votes || []
    if (currentVotes.includes(session.user.id)) return showToast('You already voted to kick this player', 'info')

    const newVotes = [...currentVotes, session.user.id]
    const allPlayerIds = [match?.creator_id, ...players.filter(p => p.status === 'accepted').map(p => p.player_id)].filter(Boolean)
    const otherPlayers = allPlayerIds.filter(pid => pid !== targetPlayerId)
    const majority = Math.floor(otherPlayers.length / 2) + 1
    const isKicked = newVotes.length >= majority

    await supabase.from('match_players').update({
      kick_votes: newVotes,
      status: isKicked ? 'kicked' : target.status,
      status_reason: isKicked ? 'Kicked by majority vote' : null,
    }).eq('id', target.id)

    if (isKicked) {
      // Lock their scorecard as-is
      const theirCard = scorecards[targetPlayerId]
      if (theirCard) {
        await supabase.from('scorecards').update({
          status: 'closed',
          closed_at: new Date().toISOString(),
          agreed_by: [...(theirCard.agreed_by || []), targetPlayerId]
        }).eq('id', theirCard.id)
      }
      showToast(`Player kicked — their scorecard is locked`, 'success')
    } else {
      showToast(`Kick vote recorded (${newVotes.length}/${majority} needed)`, 'info')
    }
    fetchAll()
  }

  const handleWithdrawMatch = async () => {
    const anyScore = Object.values(scorecards).some(sc =>
      (sc.hole_scores || []).some(s => s != null)
    )
    const msg = anyScore
      ? 'Scores have been submitted. Withdrawing will cancel your betting lines and an APP FEE will apply. Continue?'
      : 'Withdraw from this match? All your betting lines will be cancelled. No fee applies.'
    if (!window.confirm(msg)) return
    setWithdrawLoading(true)

    // Mark player as withdrawn
    const mp = players.find(p => p.player_id === session.user.id)
    if (mp) {
      await supabase.from('match_players').update({
        status: 'withdrawn',
        withdrawn_at: new Date().toISOString()
      }).eq('id', mp.id)
    }

    // Cancel all betting lines this player is in
    const { data: lines } = await supabase
      .from('betting_lines')
      .select('*')
      .eq('match_id', matchId)
      .eq('status', 'active')
      .filter('player_ids', 'cs', `{"${session.user.id}"}`)

    for (const line of lines || []) {
      const newWithdrawn = [...(line.withdrawn_by || []), session.user.id]
      const remaining = line.player_ids.filter(pid => !newWithdrawn.includes(pid))
      // Cancel if only 1 player left or withdrawal affects outcome
      const shouldCancel = remaining.length < 2
      await supabase.from('betting_lines').update({
        withdrawn_by: newWithdrawn,
        status: shouldCancel ? 'cancelled' : 'active',
        player_ids: shouldCancel ? line.player_ids : remaining,
      }).eq('id', line.id)
    }

    setWithdrawLoading(false)
    showToast('You have withdrawn from this match', 'info')
    setTimeout(() => onBack(), 1500)
  }

  const handleArchive = async () => {
    if (!window.confirm('Archive this match? It will be moved to your match history.')) return
    await supabase.from('matches').update({ state: 'archived' }).eq('id', matchId)
    onBack()
  }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading...</div>
    </div>
  )

  const isCreator = match?.creator_id === session.user.id
  const acceptedPlayers = players.filter(p => p.status === 'accepted')
  const fmt = (dt) => dt ? new Date(dt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'No tee time set'
  const scorecardsForBetting = Object.fromEntries(
    Object.entries(scorecards).map(([k, v]) => [k, v.hole_scores || []])
  )

  return (
    <div style={s.screen}>
      {toast && <div style={{ ...s.toast, background: toast.type === 'error' ? '#C0392B' : toast.type === 'success' ? '#00563F' : '#1A1814' }}>{toast.msg}</div>}

      <div style={s.body}>

        {/* Match info */}
        <div style={s.card}>
          <div style={s.infoRow}>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>TEE TIME</div>
              <div style={s.infoValue}>{fmt(match?.tee_time)}</div>
            </div>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>TYPE</div>
              <div style={s.infoValue}>{match?.match_type === 'private' ? '🔒 Private' : '🌐 Public'}</div>
            </div>
          </div>
        </div>

        {/* Course — read only */}
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle}>COURSE</div>
        </div>
        <div style={s.card}>
          <div style={s.infoRow}>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>COURSE</div>
              <div style={s.infoValue}>{match?.course_name || '—'}</div>
            </div>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>HOLES · PAR</div>
              <div style={s.infoValue}>{match?.num_holes || '—'} holes · Par {match?.course_par || '—'}</div>
            </div>
          </div>
          {match?.hole_pars?.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 4 }}>
              <div style={s.parRow}>
                {match.hole_pars.map((par, i) => (
                  <div key={i} style={s.parCell}>
                    <div style={s.parHoleNum}>{i + 1}</div>
                    <div style={s.parValue}>{par}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Players & Scorecard — merged */}
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle}>PLAYERS & SCORECARDS</div>
          {(isCreator || players.find(p => p.player_id === session.user.id)?.status === 'accepted') && <button style={s.inviteBtn} onClick={onInviteMore}>+ Invite</button>}
        </div>

        <div style={s.playerMeta}>
          <span style={s.playerMetaText}>
            {acceptedPlayers.length + 1} playing
            {players.filter(p => p.status === 'invited').length > 0 && ` · ${players.filter(p => p.status === 'invited').length} pending`}
          </span>
        </div>

        <div style={s.card}>
          <ScorecardMatrix
            match={match}
            players={[match?.creator_id, ...players.filter(p => p.status === 'accepted').map(p => p.player_id)].filter(Boolean)}
            profiles={profiles}
            scorecards={scorecardsForBetting}
            fullScorecards={scorecards}
            highlightPlayer={session.user.id}
            onEdit={onOpenScorecard}
            onKick={handleKickVote}
            matchPlayers={players}
            golfProfiles={golfProfiles}
          />
        </div>

        {/* Agree to Scorecards */}
        {(() => {
          const allPlayerIds = [match?.creator_id, ...players.filter(p => p.status === 'accepted').map(p => p.player_id)].filter(Boolean)
          const myCard = scorecards[session.user.id]
          const anyRecentEdit = allPlayerIds.some(pid => {
            const sc = scorecards[pid]
            return sc && sc.status === 'review' && (sc.agreed_by || []).length === 0 && sc.review_started_at
          })
          const iHaveAgreed = (myCard?.agreed_by || []).includes(session.user.id)
          const agreedCount = allPlayerIds.filter(pid => (scorecards[pid]?.agreed_by || []).includes(pid)).length
          const allAgreed = allPlayerIds.every(pid => (scorecards[pid]?.agreed_by || []).includes(pid))
          const canAgree = myCard && myCard.status !== 'live'

          return (
            <div style={s.agreeSection}>
              <div style={s.agreeHeader}>
                <div style={s.agreeTitle}>SCORECARD AGREEMENT</div>
                <div style={s.agreeCount}>{agreedCount}/{allPlayerIds.length} agreed</div>
              </div>
              {anyRecentEdit && !allAgreed && (
                <div style={s.editNotice}>
                  ✏️ A scorecard was recently edited — please review before agreeing
                </div>
              )}
              {allAgreed ? (
                <div style={s.allAgreedBadge}>✓ All players agreed — betting lines ready to settle</div>
              ) : iHaveAgreed ? (
                <div style={s.waitingAgree}>You agreed ✓ — waiting for others</div>
              ) : (
                <>
                  <div style={s.agreeNote}>By agreeing, you confirm all scorecards are accurate and authorize payout calculation.</div>
                  <button
                    style={{ ...s.agreeBtn, opacity: !canAgree || agreeLoading ? 0.5 : 1 }}
                    onClick={handleAgree}
                    disabled={!canAgree || agreeLoading}>
                    {agreeLoading ? 'Saving...' : !canAgree ? 'Submit your scorecard first' : '✓ Agree to All Scorecards'}
                  </button>
                </>
              )}
            </div>
          )
        })()}

        {/* Kick Vote — for unresponsive players */}
        {(() => {
          const allPlayerIds = [match?.creator_id, ...players.filter(p => p.status === 'accepted').map(p => p.player_id)].filter(Boolean)
          const hasReview = Object.values(scorecards).some(sc => sc.status === 'review' || sc.status === 'closed')
          const myCard = scorecards[session.user.id]
          const iHaveSubmitted = myCard && myCard.status !== 'live'

          if (!hasReview || !iHaveSubmitted) return null

          const unresponsive = allPlayerIds.filter(pid => {
            if (pid === session.user.id) return false
            const sc = scorecards[pid]
            const mp = players.find(p => p.player_id === pid)
            const isKicked = mp?.status === 'kicked'
            const hasAgreed = (sc?.agreed_by || []).includes(pid)
            return !isKicked && !hasAgreed
          })

          if (unresponsive.length === 0) return null

          const otherCount = allPlayerIds.filter(pid => pid !== session.user.id).length
          const majority = Math.floor(otherCount / 2) + 1

          return (
            <div style={s.kickSection}>
              <div style={s.kickTitle}>UNRESPONSIVE PLAYERS</div>
              <div style={s.kickSub}>If a player is not responding, majority can vote to kick them and proceed</div>
              {unresponsive.map(pid => {
                const prof = profiles[pid]
                const mp = players.find(p => p.player_id === pid)
                const votes = mp?.kick_votes || []
                const iVoted = votes.includes(session.user.id)
                const name = prof ? `${prof.first_name} ${prof.last_name || ''}`.trim() : '?'
                return (
                  <div key={pid} style={s.kickRow}>
                    <div style={s.kickName}>
                      {name}
                      {prof?.username && <span style={s.kickUsername}> @{prof.username}</span>}
                    </div>
                    <div style={s.kickVoteInfo}>
                      <span style={s.kickVoteCount}>{votes.length}/{majority}</span>
                      <button
                        style={{ ...s.kickBtn, opacity: iVoted ? 0.5 : 1 }}
                        onClick={() => handleKickVote(pid)}
                        disabled={iVoted}>
                        {iVoted ? 'Voted' : 'Kick'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })()}



        {/* Betting Lines */}
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle}>BETTING LINES</div>
        </div>
        <BettingLines
          session={session}
          match={match}
          players={players}
          profiles={profiles}
          scorecards={scorecardsForBetting}
          fullScorecards={scorecards}
          handicaps={handicaps}
        />

        {/* Withdraw from match */}
        {(isCreator || players.find(p => p.player_id === session.user.id)?.status === 'accepted') && (
          <button style={{ ...s.withdrawBtn, opacity: withdrawLoading ? 0.7 : 1 }} onClick={handleWithdrawMatch} disabled={withdrawLoading}>
            {withdrawLoading ? 'Withdrawing...' : 'Withdraw from Match'}
          </button>
        )}

        {/* Archive */}
        {isCreator && match?.state !== 'archived' && (
          <button style={s.archiveBtn} onClick={handleArchive}>Archive Match</button>
        )}

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  toast: { position: 'fixed', top: 0, left: 0, right: 0, padding: '16px 24px', color: 'white', fontSize: 14, fontWeight: 600, textAlign: 'center', zIndex: 100 },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  card: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  infoRow: { display: 'flex', gap: 16 },
  infoItem: { flex: 1 },
  infoLabel: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 3 },
  infoValue: { fontSize: 14, color: '#1A1814', fontWeight: 500 },
  parRow: { display: 'flex', gap: 6, minWidth: 'max-content' },
  parCell: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  parHoleNum: { fontSize: 9, color: '#7A7570', fontWeight: 700 },
  parValue: { width: 28, height: 28, background: '#FDF9E7', border: '1px solid #E0DDD4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#1A1814' },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 18, color: '#1A1814', letterSpacing: 1 },
  inviteBtn: { background: 'rgba(0,96,57,0.08)', border: '1px solid #00563F', borderRadius: 10, padding: '6px 14px', fontSize: 13, color: '#00563F', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  playerMeta: { marginTop: -6, marginBottom: 2 },
  playerMetaText: { fontSize: 12, color: '#7A7570', fontWeight: 500 },
  playerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' },
  playerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  playerRight: { display: 'flex', alignItems: 'center' },
  avatar: { width: 42, height: 42, borderRadius: 21, background: 'rgba(0,96,57,0.08)', border: '2px solid #00563F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#00563F', overflow: 'hidden', flexShrink: 0 },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  playerName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  playerSub: { fontSize: 12, color: '#7A7570', marginTop: 2 },
  youBadge: { fontSize: 10, fontWeight: 700, color: '#00563F', background: 'rgba(0,96,57,0.1)', borderRadius: 6, padding: '1px 6px', marginLeft: 6 },
  scBadge: { fontSize: 11, fontWeight: 700 },
  acceptedBadge: { width: 28, height: 28, borderRadius: 14, background: 'rgba(0,96,57,0.1)', border: '1.5px solid #00563F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#00563F', fontWeight: 700 },
  pendingBadge: { width: 28, height: 28, borderRadius: 14, background: '#F5F1E4', border: '1.5px solid #D0CCC0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 },
  scorecardBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.3, textAlign: 'center' },
  editNotice: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#9B7D52', fontWeight: 600 },
  agreeSection: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 10 },
  agreeHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  agreeTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: '#1A1814', letterSpacing: 1 },
  agreeCount: { fontSize: 12, fontWeight: 700, color: '#7A7570' },
  agreeNote: { fontSize: 12, color: '#7A7570', lineHeight: 1.6 },
  agreeBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
  allAgreedBadge: { background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#00563F', fontWeight: 700, textAlign: 'center' },
  waitingAgree: { fontSize: 13, color: '#00563F', fontWeight: 600, textAlign: 'center' },
  kickSection: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid rgba(192,57,43,0.2)', display: 'flex', flexDirection: 'column', gap: 10 },
  kickTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: '#C0392B', letterSpacing: 1 },
  kickSub: { fontSize: 12, color: '#7A7570', lineHeight: 1.5 },
  kickRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #EDE8D8' },
  kickName: { fontSize: 13, fontWeight: 700, color: '#1A1814' },
  kickUsername: { fontSize: 11, color: '#7A7570', fontWeight: 400 },
  kickVoteInfo: { display: 'flex', alignItems: 'center', gap: 8 },
  kickVoteCount: { fontSize: 12, fontWeight: 700, color: '#7A7570' },
  kickBtn: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit' },
  withdrawBtn: { background: 'transparent', color: '#C0392B', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 14, padding: '13px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
  archiveBtn: { background: 'transparent', color: '#7A7570', border: '1.5px solid #E0DDD4', borderRadius: 14, padding: '14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginTop: 8 },
}
